import { useLingui } from '@lingui/react';
import { id } from '@luminovo/commons';
import { analytics, tableSearchParams } from '@luminovo/data-stack';
import { searchParam } from '@luminovo/design-system';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { z } from 'zod/v4';

import { AppFrame } from '@/app/AppFrame';
import { AuthenticatedLayout } from '@/app/AuthenticatedLayout';
import type { RouteTo } from '@/app/movedPage';
import { isNewNavigationOn, newRouteOf, oldRouteFor, routeToOf, splatHrefAt } from '@/app/newNavigation';
import { PlainPendingComponent, RouteErrorComponent } from '@/app/routerDefaults';
import { NotAuthorizedPage } from '@/shared/permissions/NotAuthorizedPage';
import { canOpenRoute, RouteAccessDeniedError } from '@/shared/permissions/PagePermissionContext';
import type { PageStaticData } from '@/shared/routing';

// `beforeLoad` sends a signed-out user to the email form, with the URL they asked for as `redirectTo`. It
// runs before any page chunk loads, waiting for Auth0 to restore the session on a cold load (see
// `app/routerAuth.ts`).
export const Route = createFileRoute('/_authenticated')({
    // Not split: a cold signed-in load would fetch the shell only after `beforeLoad`'s wait for Auth0 and the session.
    // Router-core loads every matched chunk in one loop, so splitting would add no waterfall.
    // Splitting would cut a public page from 1,554 to 1,299 KiB gzip (measured 2026-10-04).
    codeSplitGroupings: [],
    // Sign-in only: every route beneath declares what it needs.
    staticData: { access: 'authenticated', chrome: 'none' } satisfies PageStaticData,
    // Here, not on each page: every page with comments reads `openCommentsDrawer`; tables render all over the app.
    validateSearch: z.object({
        // Opens the comments drawer of the page it is on, which removes it again (`useCommunicationsDrawer`).
        // Comment notification emails link to pages with it.
        openCommentsDrawer: searchParam.boolean('optional'),
        // `columnFilters`, `sorting` and `globalFilter`: the JSON view of a `TanStackTable` that keeps it in the URL.
        ...tableSearchParams,
    }),
    beforeLoad: async ({ context, location, matches, preload, abortController }) => {
        if (await context.auth.isSignedIn()) {
            // Routes below read it as `context.session`. Later navigations read it from the cache `useWatchSession`
            // keeps. A navigation that replaces this load while it waits reuses its token, requests and language.
            // A preload (hovering a link) loads it for the navigation it prepares, but not the language: on a public
            // page that would switch the page under the user, and `t` macro text wouldn't follow.
            const session = await context.auth.loadSession(context.queryClient, { preload });
            // `matches` is the whole branch, the routes below included; this runs on every navigation before they load.
            const leaf = matches.at(-1);
            // Before the access check, which refuses `/next` to a user who may not use the new navigation.
            const old = leaf && oldRouteFor(session, routeToOf(leaf.fullPath));
            if (leaf && old) {
                throw redirectTo(old, routeToOf(leaf.fullPath), leaf.params, location.href);
            }
            if (
                !canOpenRoute(
                    session,
                    matches.map((match) => match.staticData),
                )
            ) {
                throw new RouteAccessDeniedError();
            }
            // A redirect from here, not from each page: links keep today's URLs, and a preload follows it too.
            const moved = leaf && isNewNavigationOn(session) ? newRouteOf(routeToOf(leaf.fullPath)) : undefined;
            if (leaf && moved) {
                throw redirectTo(moved, routeToOf(leaf.fullPath), leaf.params, location.href);
            }
            return { session };
        }
        // Not for a preload, nor for a load another navigation replaced while it waited: neither redirects.
        if (!preload && !abortController.signal.aborted) {
            analytics.track(id('auth/email_form_redirected'), { source: 'before_load' });
        }
        // A `beforeLoad` redirect replaces the history entry, so Back from the email form leaves the app.
        throw redirect({
            to: '/auth/enter-email',
            search: { redirectTo: location.pathname + location.searchStr },
        });
    },
    component: RouteComponent,
    // Renders in place of the component, so the shell mounts afresh between a refused page and an allowed one.
    errorComponent: ErrorComponent,
    // In the entry chunk either way: router-plugin 1.168.40 splits a pending component only when told to.
    pendingComponent: PlainPendingComponent,
    // Shows the spinner at once while `beforeLoad` waits on a cold load, when nothing has rendered yet.
    pendingMs: 0,
});

// Keyed by the locale, the `<Outlet />` remounts open pages when it changes, the one way to reach them all from here:
// `t` reads the locale only while its component renders, and the router keeps each page's element until its match
// remounts, whatever the layout does. The navbar above only re-renders.
function RouteComponent(): JSX.Element {
    const { i18n } = useLingui();
    return (
        <AuthenticatedLayout>
            <AppFrame>
                <Outlet key={i18n.locale} />
            </AppFrame>
        </AuthenticatedLayout>
    );
}

// A refused page shows `NotAuthorizedPage` at the URL the user asked for. `AuthenticatedLayout` keeps the chat that
// page points to and the session handling, without the navbar (`AppFrame`); the page links home instead. Any other
// error gets the router's default.
function ErrorComponent({ error }: { error: unknown }): JSX.Element {
    if (error instanceof RouteAccessDeniedError) {
        return (
            <AuthenticatedLayout>
                <NotAuthorizedPage />
            </AuthenticatedLayout>
        );
    }
    return <RouteErrorComponent error={error} />;
}

/** The redirect from the page at the route `from` and the URL `href` to its other route `to`, with the same params. */
function redirectTo(to: RouteTo, from: string, params: Record<string, string>, href: string) {
    // A splat goes over as typed (`splatHrefAt`); history state goes with it, as with the params.
    const splatHref = splatHrefAt(from, to, href);
    return splatHref === undefined
        ? // `redirect` drops the query string, the fragment and the history state unless told to keep them.
          redirect({ to, params, search: true, hash: true, state: true })
        : redirect({ href: splatHref, state: true });
}
